import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Badge, Icon, tokens } from '@hotjar/hotsauce';

import {
  useSlackForwardingSettings,
  useMsTeamsForwardingSettings,
  useWebhooksForwardingSettings,
} from '@hotjar/integrations';

import type { WidgetType, WidgetId } from '@hotjar/integrations';
import { ErrorBoundary } from '@hotjar/error-handler';

const StyledLink = styled.a`
  color: inherit;
  cursor: pointer;
  display: block;
  text-align: left;
  padding: ${tokens.spacer.x2};

  &:hover {
    background: ${tokens.color.neutral_100};
    color: inherit;
  }

  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
  }
`;

const ForwardingType = styled.span`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: ${tokens.spacer.x2};
  align-items: center;
`;

const ForwardingTitle = styled.span`
  word-break: initial;
`;

interface ForwardPanelProps {
  siteId: number;
  widgetType: WidgetType;
  widgetId: WidgetId;
  onEdit: () => void;
  hasEmailForwarding: boolean;
  hasSlackForwarding: boolean;
  hasMsTeamsForwarding: boolean;
  hasWebhooksForwarding: boolean;
  emailEnabled: boolean;
}

export const ForwardPanel = ({
  siteId,
  widgetType,
  onEdit,
  hasEmailForwarding,
  hasSlackForwarding,
  hasMsTeamsForwarding,
  hasWebhooksForwarding,
  emailEnabled,
  widgetId,
}: ForwardPanelProps): JSX.Element => {
  const { t } = useTranslation();
  const [slackForwardingSettings] = useSlackForwardingSettings(siteId, widgetType, widgetId);
  const { enabled: slackEnabled } = slackForwardingSettings ?? { enabled: false };

  const msTeamsSettings = useMsTeamsForwardingSettings(
    siteId,
    widgetType,
    widgetId,
    !hasMsTeamsForwarding,
  );

  const webhooksSettings = useWebhooksForwardingSettings({
    siteId,
    widgetType,
    widgetId,
    disabled: !hasWebhooksForwarding,
  });

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <div>
        {hasSlackForwarding && (
          <StyledLink onClick={onEdit}>
            <ForwardingType>
              <Icon icon="slack" size="m" />
              <ForwardingTitle>
                {t('common:survey.responses.forwarding.slack.title')}
              </ForwardingTitle>
              <Badge kind={slackEnabled ? 'active' : 'inactive'} size="s" />
            </ForwardingType>
          </StyledLink>
        )}

        {hasMsTeamsForwarding && (
          <StyledLink onClick={onEdit}>
            <ForwardingType>
              <Icon icon="microsoft_teams" size="m" />
              <ForwardingTitle>
                {t('common:survey.responses.forwarding.ms-teams.title')}
              </ForwardingTitle>
              <Badge kind={msTeamsSettings?.enabled ? 'active' : 'inactive'} size="s" />
            </ForwardingType>
          </StyledLink>
        )}

        {hasWebhooksForwarding && (
          <StyledLink onClick={onEdit}>
            <ForwardingType>
              <Icon icon="webhooks" size="m" />
              <ForwardingTitle>
                {t('common:survey.responses.forwarding.webhooks.title')}
              </ForwardingTitle>
              <Badge kind={webhooksSettings?.enabled ? 'active' : 'inactive'} size="s" />
            </ForwardingType>
          </StyledLink>
        )}

        {hasEmailForwarding && (
          <StyledLink onClick={onEdit}>
            <ForwardingType>
              <Icon icon="email" size="m" />
              <ForwardingTitle>
                {t('common:survey.responses.forwarding.email.title')}
              </ForwardingTitle>
              <Badge kind={emailEnabled ? 'active' : 'inactive'} size="s" />
            </ForwardingType>
          </StyledLink>
        )}
      </div>
    </ErrorBoundary>
  );
};
